import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    FileText, 
    Download, 
    File,
    Image,
    CheckCircle,
    Clock,
    AlertCircle,
    Tag,
    Filter,
    X,
    ChevronDown,
    ChevronUp,
    Paperclip
} from 'react-feather';
import { projectApi, taskApi } from '../services/api';
import { Button } from '../components/ui/button';
import { FeatherArrowLeft } from '../components/ui/icons';
import TaskTypeIcon from '../components/shared/TaskTypeIcon';
import { useAuth } from '../context/AuthContext';
import FeatureTagDetailModal from '../components/FeatureTags/FeatureTagDetailModal';
import TaskTypeSelect from '../components/shared/TaskTypeSelect';
import './ProjectNotesArtifactsView.css';

// Helper function for status badge classes
const getStatusBadgeClass = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'in-progress':
            return 'status-badge status-in-progress';
        case 'completed':
            return 'status-badge status-completed';
        case 'released':
            return 'status-badge status-released';
        case 'open':
        default:
            return 'status-badge status-open';
    }
};

const ProjectNotesArtifactsView = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [notes, setNotes] = useState('');
    const [artifacts, setArtifacts] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [peerTasks, setPeerTasks] = useState([]);
    const [loadingPeerTasks, setLoadingPeerTasks] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Filter states
    const [filters, setFilters] = useState({
        status: [],
        taskType: '',
        assignedTo: '',
        featureTag: ''
    });
    const [groupByFeatureTag, setGroupByFeatureTag] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
    const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);

    useEffect(() => {
        if (projectId) {
            fetchData();
        }
    }, [projectId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch project details
            const projectData = await projectApi.get(projectId);
            setProject(projectData);

            // Fetch notes
            const notesData = await projectApi.getProjectNotes(projectId);
            setNotes(notesData.notes || '');

            // Fetch artifacts
            const artifactsData = await projectApi.getArtifacts(projectId);
            setArtifacts(artifactsData || []);

            // Fetch tasks
            const tasksData = await taskApi.getByProject(projectId);
            // Filter out work_plan_tasks data (managerial info)
            const cleanTasks = (tasksData || []).map(task => {
                const { work_plan_tasks, hours, work_plan_notes, ...cleanTask } = task;
                return cleanTask;
            });
            setTasks(cleanTasks);

            // Fetch peer tasks (today's work plan tasks by other users)
            await fetchPeerTasks(projectId);
        } catch (err) {
            console.error('Error fetching project data:', err);
            if (err.response?.status === 403) {
                setError('You do not have access to this project');
            } else if (err.response?.status === 404) {
                setError('Project not found');
            } else {
                setError('Failed to load notes and artifacts');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchPeerTasks = async (projectIdToUse) => {
        if (!projectIdToUse) return;
        try {
            setLoadingPeerTasks(true);
            const data = await projectApi.getTodayPeerTasks(projectIdToUse);
            // Exclude current user as a safety check (backend already excludes)
            const filtered = (data || []).filter(pt => pt.user?.id !== user?.id);
            setPeerTasks(filtered);
        } catch (err) {
            console.error('Error fetching peer tasks:', err);
            setPeerTasks([]);
        } finally {
            setLoadingPeerTasks(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) {
            return <Image size={18} />;
        } else if (fileType === 'application/pdf') {
            return <FileText size={18} />;
        } else {
            return <File size={18} />;
        }
    };

    const getUserInitial = (name = '', email = '') => {
        const source = name || email || '?';
        return source.charAt(0).toUpperCase() || '?';
    };

    const groupedPeerTasks = useMemo(() => {
        const groups = {};
        peerTasks.forEach(entry => {
            const userId = entry.user?.id || 'unknown';
            if (!groups[userId]) {
                groups[userId] = {
                    user: entry.user,
                    tasks: []
                };
            }
            groups[userId].tasks.push(entry);
        });
        return groups;
    }, [peerTasks]);

    // Check if feature tag is overdue
    const isFeatureOverdue = (featureTag) => {
        if (!featureTag?.due_date) return false;
        return new Date(featureTag.due_date) < new Date();
    };

    // Get unique values for filters
    const uniqueAssignees = useMemo(() => {
        const assignees = new Map();
        tasks.forEach(task => {
            if (task.assigned_to?.id) {
                assignees.set(task.assigned_to.id, task.assigned_to);
            }
        });
        return Array.from(assignees.values());
    }, [tasks]);

    // Get unique users from tasks for the feature tag modal
    const uniqueUsers = useMemo(() => {
        const usersMap = new Map();
        tasks.forEach(task => {
            if (task.assigned_to?.id) {
                usersMap.set(task.assigned_to.id, task.assigned_to);
            }
            if (task.created_by?.id) {
                usersMap.set(task.created_by.id, task.created_by);
            }
        });
        return Array.from(usersMap.values());
    }, [tasks]);

    const uniqueFeatureTags = useMemo(() => {
        const tags = new Map();
        tasks.forEach(task => {
            if (task.feature_tag?.id) {
                tags.set(task.feature_tag.id, task.feature_tag);
            }
        });
        return Array.from(tags.values());
    }, [tasks]);

    // Filter tasks
    const filteredTasks = useMemo(() => {
        let filtered = tasks;

        // Filter by status
        if (filters.status.length > 0) {
            filtered = filtered.filter(task => filters.status.includes(task.status));
        }

        // Filter by task type
        if (filters.taskType) {
            filtered = filtered.filter(task => (task.task_type || 'Task') === filters.taskType);
        }

        // Filter by assigned to
        if (filters.assignedTo) {
            if (filters.assignedTo === 'unassigned') {
                filtered = filtered.filter(task => !task.assigned_to?.id);
            } else {
                filtered = filtered.filter(task => task.assigned_to?.id === filters.assignedTo);
            }
        }

        // Filter by feature tag
        if (filters.featureTag) {
            if (filters.featureTag === 'untagged') {
                filtered = filtered.filter(task => !task.feature_tag?.id);
            } else {
                filtered = filtered.filter(task => task.feature_tag?.id === filters.featureTag);
            }
        }

        return filtered;
    }, [tasks, filters]);

    // Group tasks by feature tag
    const groupedTasks = useMemo(() => {
        if (!groupByFeatureTag) {
            return { 'all': { tag: null, tasks: filteredTasks } };
        }

        const groups = {};
        filteredTasks.forEach(task => {
            const tagId = task.feature_tag?.id || 'untagged';
            if (!groups[tagId]) {
                groups[tagId] = {
                    tag: task.feature_tag || { name: 'Untagged', color: '#6B7280', id: 'untagged' },
                    tasks: []
                };
            }
            groups[tagId].tasks.push(task);
        });

        // Sort tasks within each group by created date (newest first)
        Object.values(groups).forEach(group => {
            group.tasks.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });

        return groups;
    }, [filteredTasks, groupByFeatureTag]);

    // Initialize expanded groups
    useEffect(() => {
        if (groupByFeatureTag && Object.keys(groupedTasks).length > 0) {
            setExpandedGroups(new Set(Object.keys(groupedTasks)));
        }
    }, [groupByFeatureTag, groupedTasks]);

    const toggleGroup = (groupId) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const handleFeatureTagClick = (e, featureTag) => {
        e.stopPropagation(); // Prevent event bubbling (e.g., toggling group)
        if (featureTag && featureTag.id) {
            setSelectedFeatureTag(featureTag);
            setIsFeatureTagModalOpen(true);
        }
    };

    const handleStatusFilterToggle = (status) => {
        setFilters(prev => {
            const currentStatuses = prev.status || [];
            const newStatuses = currentStatuses.includes(status)
                ? currentStatuses.filter(s => s !== status)
                : [...currentStatuses, status];
            return { ...prev, status: newStatuses };
        });
    };

    const clearFilters = () => {
        setFilters({
            status: [],
            taskType: '',
            assignedTo: '',
            featureTag: ''
        });
    };

    const hasActiveFilters = filters.status.length > 0 || filters.taskType || filters.assignedTo || filters.featureTag;

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="project-notes-artifacts-view-page">
                <div className="project-notes-artifacts-view-loading">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="project-notes-artifacts-view-page">
                <div className="project-notes-artifacts-view-header">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate('/my-projects')}
                        className="back-button"
                    >
                        Back to My Projects
                    </Button>
                </div>
                <div className="project-notes-artifacts-view-error">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="project-notes-artifacts-view-page">
            <div className="project-notes-artifacts-view-header">
                <Button
                    variant="neutral-tertiary"
                    icon={<FeatherArrowLeft />}
                    onClick={() => navigate('/my-projects')}
                    className="back-button"
                >
                    Back to My Projects
                </Button>
                {project && (
                    <div className="project-notes-artifacts-view-header-content">
                        <h1 className="project-notes-artifacts-view-title">{project.name}</h1>
                        {project.abbreviation && (
                            <span className="project-notes-artifacts-view-badge">{project.abbreviation}</span>
                        )}
                    </div>
                )}
            </div>

            <div className="project-notes-artifacts-view-readonly-banner">
                <FileText size={16} />
                <span>Read-only view - You can view notes and artifacts but cannot edit them</span>
            </div>

            {/* Notes Section */}
            <div className="project-notes-section">
                <div className="project-notes-header">
                    <h3 className="project-notes-title">
                        <FileText size={18} />
                        Project Notes
                    </h3>
                </div>
                <div className="project-notes-viewer">
                    {notes ? (
                        <pre className="project-notes-content">{notes}</pre>
                    ) : (
                        <p className="project-notes-empty">No notes available for this project.</p>
                    )}
                </div>
            </div>

            {/* Artifacts Section */}
            <div className="project-artifacts-section">
                <div className="project-artifacts-header">
                    <h3 className="project-artifacts-title">
                        <File size={18} />
                        Project Artifacts
                    </h3>
                </div>

                {artifacts.length === 0 ? (
                    <div className="project-artifacts-empty">
                        <File size={48} />
                        <p>No artifacts uploaded yet</p>
                    </div>
                ) : (
                    <div className="project-artifacts-list">
                        {artifacts.map((artifact) => (
                            <div key={artifact.id} className="project-artifact-item">
                                <div className="project-artifact-icon">
                                    {getFileIcon(artifact.file_type)}
                                </div>
                                <div className="project-artifact-info">
                                    <div className="project-artifact-name">{artifact.file_name}</div>
                                    <div className="project-artifact-meta">
                                        <span>{formatFileSize(artifact.file_size)}</span>
                                        <span>•</span>
                                        <span>
                                            {new Date(artifact.created_at).toLocaleDateString()}
                                        </span>
                                        {artifact.uploaded_by_user && (
                                            <>
                                                <span>•</span>
                                                <span>
                                                    {artifact.uploaded_by_user.display_name || artifact.uploaded_by_user.email}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="project-artifact-actions">
                                    <Button
                                        variant="brand-primary"
                                        onClick={() => window.open(artifact.url, '_blank')}
                                        title="Download"
                                    >
                                        <Download size={16} />
                                        Download
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Peer Tasks Section */}
            <div className="peer-tasks-section">
                <div className="peer-tasks-header">
                    <h3 className="peer-tasks-title">
                        <CheckCircle size={16} />
                        Tasks Picked Up by Peers Today
                    </h3>
                </div>
                {loadingPeerTasks ? (
                    <div className="peer-tasks-loading">Loading peer tasks...</div>
                ) : peerTasks.length === 0 ? (
                    <div className="peer-tasks-empty">
                        <p>No peer tasks picked up today.</p>
                    </div>
                ) : (
                    <div className="peer-tasks-list">
                        {Object.entries(groupedPeerTasks).map(([groupId, group]) => (
                            <div key={groupId} className="peer-task-user-group">
                                <div className="peer-task-user">
                                    <div className="peer-task-avatar">
                                        {getUserInitial(group.user?.display_name, group.user?.email)}
                                    </div>
                                    <div className="peer-task-user-meta">
                                        <div className="peer-task-user-name">
                                            {group.user?.display_name || group.user?.email || 'Unknown User'}
                                        </div>
                                        {group.user?.email && (
                                            <div className="peer-task-user-email">{group.user.email}</div>
                                        )}
                                    </div>
                                </div>
                                <div className="peer-task-user-tasks">
                                    {group.tasks.map((entry) => (
                                        <div key={`${groupId}-${entry.task?.id}`} className="peer-task-item">
                                            <div className="peer-task-info">
                                                <span className="peer-task-id">
                                                    [{entry.task?.display_id || '---'}]
                                                </span>
                                                <span className="peer-task-title">
                                                    {entry.task?.title || 'Untitled Task'}
                                                </span>
                                            </div>
                                            <div className="peer-task-meta">
                                                <span className={getStatusBadgeClass(entry.task?.status)}>
                                                    {entry.task?.status || 'open'}
                                                </span>
                                                {entry.task?.task_type && (
                                                    <span className="peer-task-type">
                                                        <TaskTypeIcon taskType={entry.task.task_type} size={14} />
                                                        {entry.task.task_type}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Tasks Section */}
            <div className="project-tasks-section">
                <div className="project-tasks-header">
                    <h3 className="project-tasks-title">
                        <CheckCircle size={18} />
                        Project Tasks
                    </h3>
                    <div className="project-tasks-controls">
                        <button
                            className={`group-toggle-btn ${groupByFeatureTag ? 'active' : ''}`}
                            onClick={() => setGroupByFeatureTag(!groupByFeatureTag)}
                            title={groupByFeatureTag ? 'Ungroup tasks' : 'Group by feature tags'}
                        >
                            <Tag size={16} />
                            {groupByFeatureTag ? 'Grouped' : 'Ungrouped'}
                        </button>
                    </div>
                </div>

                {/* Filter Controls */}
                <div className="project-tasks-filters">
                    <div className="filter-group">
                        <label className="filter-label">Status</label>
                        <div className="filter-checkboxes">
                            {['open', 'in-progress', 'completed', 'released'].map(status => (
                                <label key={status} className="filter-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={filters.status.includes(status)}
                                        onChange={() => handleStatusFilterToggle(status)}
                                    />
                                    <span>{status.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Task Type</label>
                        <TaskTypeSelect
                            value={filters.taskType}
                            onChange={(e) => setFilters(prev => ({ ...prev, taskType: e.target.value }))}
                            className="filter-select"
                            includeAllOption
                        />
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Assigned To</label>
                        <select
                            value={filters.assignedTo}
                            onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                            className="filter-select"
                        >
                            <option value="">All Users</option>
                            <option value="unassigned">Unassigned</option>
                            {uniqueAssignees.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.display_name || user.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label className="filter-label">Feature Tag</label>
                        <select
                            value={filters.featureTag}
                            onChange={(e) => setFilters(prev => ({ ...prev, featureTag: e.target.value }))}
                            className="filter-select"
                        >
                            <option value="">All Tags</option>
                            <option value="untagged">Untagged</option>
                            {uniqueFeatureTags.map(tag => (
                                <option key={tag.id} value={tag.id}>
                                    {tag.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {hasActiveFilters && (
                        <button className="clear-filters-btn" onClick={clearFilters}>
                            <X size={14} />
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Task Count */}
                <div className="project-tasks-count">
                    Showing {filteredTasks.length} of {tasks.length} tasks
                </div>

                {/* Tasks Display */}
                {filteredTasks.length === 0 ? (
                    <div className="project-tasks-empty">
                        <CheckCircle size={48} />
                        <p>No tasks found</p>
                        {hasActiveFilters && (
                            <p className="project-tasks-empty-hint">Try adjusting your filters</p>
                        )}
                    </div>
                ) : (
                    <div className="project-tasks-list">
                        {groupByFeatureTag ? (
                            // Grouped view
                            Object.entries(groupedTasks).map(([groupId, group]) => (
                                <div key={groupId} className="task-group">
                                    <div 
                                        className="task-group-header"
                                        onClick={() => toggleGroup(groupId)}
                                    >
                                        <div className="task-group-title">
                                            {group.tag && (
                                                <span 
                                                    className="task-group-tag-indicator"
                                                    style={{ backgroundColor: group.tag.color || '#6B7280' }}
                                                />
                                            )}
                                            {group.tag && group.tag.id ? (
                                                <span 
                                                    className="task-group-name clickable-feature-tag"
                                                    onClick={(e) => handleFeatureTagClick(e, group.tag)}
                                                    title="Click to view feature details"
                                                >
                                                    {group.tag.name}
                                                </span>
                                            ) : (
                                                <span className="task-group-name">
                                                    {group.tag?.name || 'Untagged'}
                                                </span>
                                            )}
                                            {isFeatureOverdue(group.tag) && (
                                                <span className="task-group-overdue-badge">Overdue</span>
                                            )}
                                            <span className="task-group-count">({group.tasks.length})</span>
                                        </div>
                                        {expandedGroups.has(groupId) ? (
                                            <ChevronUp size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </div>
                                    {expandedGroups.has(groupId) && (
                                        <div className="task-group-content">
                                            {group.tasks.map(task => (
                                                <TaskItem 
                                                    key={task.id} 
                                                    task={task} 
                                                    isFeatureOverdue={isFeatureOverdue(task.feature_tag)}
                                                    onFeatureTagClick={handleFeatureTagClick}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            // Ungrouped view
                            filteredTasks.map(task => (
                                <TaskItem 
                                    key={task.id} 
                                    task={task} 
                                    isFeatureOverdue={isFeatureOverdue(task.feature_tag)}
                                    onFeatureTagClick={handleFeatureTagClick}
                                />
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Feature Tag Detail Modal */}
            {selectedFeatureTag && (
                <FeatureTagDetailModal
                    tag={selectedFeatureTag}
                    isOpen={isFeatureTagModalOpen}
                    onClose={() => {
                        setIsFeatureTagModalOpen(false);
                        setSelectedFeatureTag(null);
                    }}
                    tasks={tasks}
                    users={uniqueUsers}
                />
            )}
        </div>
    );
};

// Task Item Component
const TaskItem = ({ task, isFeatureOverdue, onFeatureTagClick }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="project-task-item">
            <div className="project-task-header">
                <div className="project-task-title-section">
                    <TaskTypeIcon taskType={task.task_type || 'Task'} size={18} />
                    <div>
                        <div className="project-task-title-row">
                            <span className="project-task-display-id">{task.display_id}</span>
                            <h4 className="project-task-title">{task.title}</h4>
                        </div>
                        <div className="project-task-meta-row">
                            <span className={getStatusBadgeClass(task.status)}>
                                {task.status?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Open'}
                            </span>
                            <span className="project-task-type">{task.task_type || 'Task'}</span>
                            {task.feature_tag && (
                                <>
                                    <span 
                                        className="project-task-tag clickable-feature-tag"
                                        onClick={(e) => onFeatureTagClick && onFeatureTagClick(e, task.feature_tag)}
                                        title="Click to view feature details"
                                    >
                                        <span 
                                            className="project-task-tag-color"
                                            style={{ backgroundColor: task.feature_tag.color || '#6B7280' }}
                                        />
                                        {task.feature_tag.name}
                                    </span>
                                    {isFeatureOverdue && (
                                        <span className="project-task-overdue-badge">Feature Overdue</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {task.description && (
                <div className="project-task-description">
                    {task.description}
                </div>
            )}

            <div className="project-task-details">
                <div className="project-task-detail-row">
                    <div className="project-task-detail-item">
                        <span className="project-task-detail-label">Assigned To:</span>
                        <span className="project-task-detail-value">
                            {task.assigned_to ? (task.assigned_to.display_name || task.assigned_to.email) : 'Unassigned'}
                        </span>
                    </div>
                    <div className="project-task-detail-item">
                        <span className="project-task-detail-label">Assigned Date:</span>
                        <span className="project-task-detail-value">
                            {task.assigned_to ? formatDate(task.updated_at) : 'N/A'}
                        </span>
                    </div>
                </div>
                <div className="project-task-detail-row">
                    <div className="project-task-detail-item">
                        <span className="project-task-detail-label">Created By:</span>
                        <span className="project-task-detail-value">
                            {task.created_by ? (task.created_by.display_name || task.created_by.email) : 'Unknown'}
                        </span>
                    </div>
                    <div className="project-task-detail-item">
                        <span className="project-task-detail-label">Created Date:</span>
                        <span className="project-task-detail-value">
                            {formatDate(task.created_at)}
                        </span>
                    </div>
                </div>
            </div>

            {task.attachments && task.attachments.length > 0 && (
                <div className="project-task-attachments">
                    <Paperclip size={14} />
                    <span className="project-task-attachments-label">Attachments ({task.attachments.length}):</span>
                    <div className="project-task-attachments-list">
                        {task.attachments.map((attachment, idx) => (
                            <a
                                key={attachment.id || idx}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="project-task-attachment-link"
                            >
                                <Download size={12} />
                                {attachment.file_name}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectNotesArtifactsView;

